<template>
	<main class="workflow-view">
		<pdf-header :pdfHeaderData="pdfHeaderData" />
		<workflow-header
			:saveBtnAttr="saveBtnAttr"
			:saveButtonEnabled="workflowsLoaded"
			:enableShareUrl="getEnableShareUrl"
			:labNotesActive="getLabNotesActive"
			:labNotesLockActive="getLabNotesLockActive"
			:tourActive="getTourActive"
			:isSavedWorkflow="isSavedWorkflow"
			:isSharedWorkflow="isSharedWorkflow"
			@saveWorkflow="saveWorkflowInit"
			@showMyWorkflows="showMyWorkflows"
			@downloadCSV="downloadCSV"
			@downloadPDF="downloadPDF"
			@shareWorkflow="openShareWorkflowModal"
			@setLabNotesActive="setLabNotesActive"
			@resetWorkflow="openResetWorkflowModal"
		/>
		<section class="workflow-body">
			<workflow-step
				v-for="(workflow, index) in renderWorkFlowSteps"
				:workflow="workflow"
				:country="getCountry"
				:numberOfSteps="renderWorkFlowSteps.length"
				:stepNumber="++index"
				:key="index + '-step'"
				:labNotesActive="getLabNotesActive"
				:tourActive="getTourActive"
				:isTabletView="getisTabletView"
				:isCustomStep="workflow.customContent"
				@requestDemo="requestDemo"
				@sendToInquireLink="sendToInquireLink"
				@sendToGeneGlobeLink="sendToGeneGlobeLink"
				@showProductOptions="toggleOptionsModal"
				@addStep="showAddStepModal"
				@logInRequested="sendToLogin(false)"
				:data-test="`step-${workflow.name}`"
			/>
		</section>
		<!-- cta:start -->
		<section
			data-print="enabled"
			v-show="!getLabNotesActive"
			class="cta-section"
			data-cy="what-is-your-next-steps-section"
		>
			<header>
				<h5 class="H5-5">What is your next step?</h5>
			</header>
			<div class="cta-row cta-1">
				<workflow-cta
					v-for="(cta, i) in ctas"
					:key="i"
					v-bind="cta"
					@btnClicked="cta.handleClick"
					:enableShareUrl="getEnableShareUrl"
				/>
				<web-component
					ref="add-all-cta"
					_selector="wca-add-to-cart"
					cart-endpoint="/product-fragments-api/cart"
					button-style="mat-raised-button"
					:products="JSON.stringify(allProductsForCartButton)"
					style="visibility: hidden"
				></web-component>
			</div>
		</section>
		<!-- cta:end -->

		<section class="interests-section" :class="{'is-lab-note-mode': getLabNotesActive}">
			<header class="interests-header mobile-hide">
				<h3>Featured</h3>
			</header>
			<article class="interests-body">
				<div class="interests-card" v-for="(item, index) in interests" :key="index">
					<img :src="require(`@/assets/images/${item.img}`)" :alt="item.title" target="_blank" />
					<div class="interests-card-details">
						<header>
							<h5>{{ item.title }}</h5>
						</header>
						<p class="body-1 mobile-body-1">{{ item.body }}</p>
						<md-button
							@click="tracking(item.link.text)"
							class="md-primary md-button-large interests-link"
							:href="interestsLink(item.link.src)"
						>
							{{ item.link.text }}
						</md-button>
					</div>
				</div>
			</article>
		</section>
		<!-- warning: start -->
		<div class="warning-section">
			<workflow-disclaimer />
		</div>
		<!-- warning: end -->

		<!-- modal:start -->
		<AddStepModal
			v-if="addStepModal.state"
			:modalOpen="addStepModal.state"
			class="add-workflow-step-modal"
			@close="$emit('close')"
		>
			<template v-slot:header>
				<div class="header">
					<h3>Add experiment step</h3>
				</div>
			</template>

			<ValidationObserver ref="customStep">
				<ValidationProvider name="add-workflow-step-name" rules="customStepValidation" v-slot="{errors}">
					<input-area
						name="add-workflow-step-name"
						id="add-workflow-step-name"
						label="Experiment step title"
						maxlength="300"
						tag="input"
						v-model="addStepModal.name"
						:isVisible="addStepModal.state"
						:error="errors[0]"
					/>
				</ValidationProvider>

				<ValidationProvider name="add-workflow-step-description" rules="customStepValidation" v-slot="{errors}">
					<input-area
						name="add-workflow-step-description"
						id="add-workflow-step-description"
						label="Description"
						maxlength="600"
						tag="textarea"
						v-model="addStepModal.desc"
						:error="errors[0]"
					/>
				</ValidationProvider>
			</ValidationObserver>

			<template v-slot:footer>
				<div class="add-step-footer">
					<md-button class="md-primary md-button-large" @click="cancelAddWorkflowStep">cancel </md-button>
					<md-button class="md-primary md-raised md-button-large" @click="validateAndAddWorkflowStep">
						<base-icon class="check" icon-name="check" />
						Add experiment step
					</md-button>
				</div>
			</template>
		</AddStepModal>
		<!-- modal:end -->
		<!-- optionsModal: start -->

		<transition name="slide-up" mode="in-out">
			<!-- TODO: this component (OptionsModal) needs to be programmed to
        check product information for `:lang`, and then fall back to enUS if there
        is no product information for `:lang`. Presently it just looks at enUS,
        which is fine since we globally set the language to enUS. This change is
        required to enable non-English language support -->
			<OptionsModal
				v-if="optionsModalIsOpen"
				:options="getOptionsModal"
				@requestDemo="requestDemo"
				@sendToInquireLink="sendToInquireLink"
				@sendToGeneGlobeLink="sendToGeneGlobeLink"
				@showCustomProductModal="showCustomProductModal"
				@customProductVal="setCustomProductVal"
				@logInRequested="logInFromOptionsModal"
				:lang="LANG"
				@closeOptionsModal="toggleOptionsModal"
				@doWeShowRuleModal="doWeShowRuleModal"
			/>
		</transition>
		<!-- optionsModal: end -->
		<!-- customProductModal: start -->
		<custom-product-modal
			v-if="openCustomProductModal"
			:modalOpen="openCustomProductModal"
			:customProductData="customProductData"
			@customProductVal="setCustomProductVal"
			@close="triggerCustomProductModal"
		/>
		<want-to-leave-modal
			v-show="openWantToLeaveModal"
			:modalOpen="openWantToLeaveModal"
			@close="openWantToLeaveModal = false"
		/>
		<!-- WantToLeaveModal: end -->
		<!-- PreLoginModal: start -->
		<pre-login-modal
			v-if="preLoginModal"
			:preLoginData="preLoginModalMessage"
			@close="
				preLoginModal = false;
				requestQuoteSelected = false;
				requestGenomicSelected = false;
				shareWorkflowSelected = false;
				labNotesSelected = false;
			"
			@sendToLogin="sendToLogin"
			:registerLink="registerLink"
		/>
		<!-- workflow save error snackbar: start -->
		<snack-bar @snackbar-active="snackbarActive = $event" @handle-snackbar="saveWorkflowInit" />
		<!-- requestModal: start -->
		<transition name="slide-up" mode="in-out">
			<requests-modal
				v-if="showRequestsModal"
				:workflowSteps="renderWorkFlowSteps"
				:country="getCountry"
				:genomic="isGenomic"
				@backButton="requestBackToWorkflow"
			/>
		</transition>
		<!-- requestModal: end -->
		<!-- Products AdaptationModal: start -->
		<products-adaptation-modal
			v-if="openProductsAdaptationModal"
			:modalOpen="openProductsAdaptationModal"
			:removeProducts="getRuleRemovedProducts"
			:addProducts="getRuleAddedProducts"
			@accept="accecptProductRuleChanges"
			@close="() => (openProductsAdaptationModal = false)"
		/>
		<!-- Products AdaptationModal: end -->
		<RemoveProductModal @doWeShowRuleModal="doWeShowRuleModal" />
		<ResetWorkflowModal
			:isModalOpen="isResetWorkflowModalOpen"
			@closeModal="closeResetWorkflowModal"
			@resetWorkflow="resetWorkflow"
		/>
	</main>
</template>

<script>
import {buildLsObjForBackend} from '@/services/build-object-to-backend.js';
import {getWebshopData} from '@/util/getWebshopData.js';
import {interests} from '@/util/interests.js';
import {pdfReadFeature} from '@/util/pdfReadFeature.js';
import {preLoginData} from '@/util/preLoginData.js';
import {createObject, trackEvent, trackObject} from '@/services/tracking.js';
import {extend, ValidationObserver, ValidationProvider} from 'vee-validate';
import clonedeep from 'lodash.clonedeep';
import isequal from 'lodash.isequal';
import loggingDevInfo from '@/services/log-dev-info';
import CustomProductModal from '@/components/modals/CustomProductModal.vue';
import OptionsModal from '@/components/modals/optionsModal/OptionsModal.vue';
import WantToLeaveModal from '@/components/modals/WantToLeaveModal.vue';
import WorkflowHeader from '@/components/workflow/WorkflowHeader.vue';
import PreLoginModal from '@/components/modals/PreLoginModal.vue';
import RequestsModal from '@/components/modals/RequestsModal.vue';
import RemoveProductModal from '@/components/modals/RemoveProductModal.vue';
import ResetWorkflowModal from '@/components/modals/ResetWorkflowModal.vue';
import ProductsAdaptationModal from '@/components/modals/ProductsAdaptationModal.vue';
import WorkflowStep from '@/components/workflow/workflowStep/WorkflowStep.vue';
import WorkflowCta from '@/components/workflow/WorkflowCta.vue';
import PdfHeader from '@/components/workflow/PdfHeader.vue';
import workflowDisclaimer from '@/shared/workflowDisclaimer.vue';
// order matters for mixins
import checkForRuleChanges from '@/components/workflow/mixins/checkForRuleChanges';
import findSelection from '@/mixins/findSelection';
import tour from '@/mixins/tour';
import cartActions from '@/components/workflow/mixins/cartActions';
import addWorkflowStep from '@/components/workflow/mixins/addWorkflowStep';
import snackbars from '@/components/workflow/mixins/snackbars';
import saveWorkflow from '@/components/workflow/mixins/saveWorkflow';
import saveToLocalStorage from '@/components/workflow/mixins/saveToLocalStorage';
import workflowFromMyWorkflows from '@/components/workflow/mixins/workflowFromMyWorkflows';
import callToApiBackend from '@/components/workflow/mixins/callToApiBackend';
import workflowCtas from '@/components/workflow/mixins/workflowCtas';

extend('customStepValidation', {
	message: 'This field is required.',
	validate(value) {
		return {
			valid: value?.length > 0,
		};
	},
	computesRequired: true,
});

export default {
	name: 'Workflow',
	props: {
		application: {
			required: true,
			type: String,
		},
		startingMaterial: {
			required: true,
			type: String,
		},
		analyte: {
			required: true,
			type: String,
		},
		technology: {
			required: true,
			type: String,
		},
	},
	components: {
		OptionsModal,
		WorkflowHeader,
		WorkflowStep,
		WorkflowCta,
		CustomProductModal,
		WantToLeaveModal,
		PreLoginModal,
		ProductsAdaptationModal,
		PdfHeader,
		RequestsModal,
		RemoveProductModal,
		workflowDisclaimer,
		ResetWorkflowModal,
		ValidationProvider,
		ValidationObserver,
	},
	mixins: [
		checkForRuleChanges,
		findSelection,
		addWorkflowStep,
		callToApiBackend,
		cartActions,
		snackbars,
		saveToLocalStorage,
		saveWorkflow,
		workflowFromMyWorkflows,
		workflowCtas,
		tour,
	],
	beforeRouteLeave(to, from, next) {
		this.checkClosePopup(next, next);
	},
	data() {
		return {
			loading: true,
			notFound: true,
			optionsModalIsOpen: false,
			optionsModalKey: null,
			partialWorkFlowKey: '',
			productsWorkflow: null,
			CVLWBSteps: null,
			CVLFilter: null,
			Products: null,
			workFlowSteps: [],
			modalOptions: {},
			LANG: null,
			preLoginData,
			interests,
			openCustomProductModal: false,
			customProductData: null,
			openWantToLeaveModal: false,
			openProductsAdaptationModal: false,
			myWorkflowsView: false,
			preLoginModal: false,
			isGenomic: false,
			showRequestsModal: false,
			requestQuoteSelected: false,
			requestGenomicSelected: false,
			shareWorkflowSelected: false,
			labNotesActive: false,
			labNotesLockActive: true,
			labNotesSelected: false,
			pageIsReady: false,
			isResetWorkflowModalOpen: false,
			isSavedWorkflow: false,
			isSharedWorkflow: false,
			elnTour: false,
		};
	},
	watch: {
		$route(newRoute) {
			if (!newRoute.query.modal) this.checkClosePopup();
		},
		showRequestsModal(newVal) {
			if (!newVal) {
				this.sendToAnalytics('workflow overview');
				this.removeQueryModal();
			} else {
				this.addQueryModal();
				const pageType = !this.isGenomic ? 'contact sales' : 'request genomic services';
				this.sendToAnalytics(pageType);
			}
			this.toggleBodyScroll();
		},
		restoreOptionsModal(newVal) {
			if (newVal) {
				this.restoreOptionsModalAfterLogin();
			}
		},
		pageIsReady() {
			if (pdfReadFeature.get()) {
				pdfReadFeature.disable();
				setTimeout(() => {
					this.downloadPDF();
				}, 1000);
			}
		},
		labNotesActive() {
			if (this.labNotesActive) {
				this.elnTour = true;
			}
		},
		async activeWorkflowTags(newV, oldV) {
			if (!isequal(newV, oldV)) {
				await this.onTagsSelectionChange();
			}
		},
		async activeStepTags(newV, oldV) {
			if (!isequal(newV, oldV)) {
				await this.onTagsSelectionChange();
			}
		},
	},
	computed: {
		getTourActive() {
			return this.$store.getters['tour/getTourFlag'];
		},
		getisTabletView() {
			return this.$store.getters['tour/getisTabletView'];
		},
		getLabNotesActive() {
			return this.labNotesActive;
		},
		getLabNotesLockActive() {
			return !this.checkIfLoggedIn();
		},
		getEnableShareUrl() {
			return this.$store.state.enableShareUrl;
		},
		storeStepsList() {
			return this.$store.state.cvlWebSteps.values;
		},
		getLoggedInStatus() {
			return this.$store.getters.getLoggedInStatus;
		},
		preLoginModalMessage() {
			if (this.requestGenomicSelected) {
				return this.preLoginData.requestGenomic;
			}
			if (this.requestQuoteSelected) {
				return this.preLoginData.requestQuote;
			}
			if (this.shareWorkflowSelected) {
				return this.preLoginData.shareWorkflow;
			}
			if (this.labNotesSelected) {
				return this.preLoginData.activateLabDocumentation;
			}

			return this.preLoginData.saveWorkflow;
		},
		renderWorkFlowSteps() {
			const completeWorkflow = this.$store.getters['workflow/wfSteps'].sort((a, b) => a.index - b.index);
			this.$root.$emit('checkForRuleChanges', completeWorkflow);
			return completeWorkflow;
		},
		getOptionsModal() {
			return this.modalOptions;
		},
		getCountry() {
			return this.$store.state.jwt ? this.$store.state.jwt.country : this.$store.state.COUNTRY;
		},
		getRuleAddedProducts() {
			return this.ruleAddedProducts;
		},
		getRuleRemovedProducts() {
			return this.ruleRemovedProducts;
		},
		workflowsLoaded() {
			return this.renderWorkFlowSteps.length > 0;
		},
		getWorkflowKey() {
			const {entryPointsList} = this.$store.state.hcube;
			return `${entryPointsList.indexOf(this.application)}.${entryPointsList.indexOf(
				this.startingMaterial,
			)}.${entryPointsList.indexOf(this.analyte)}.${entryPointsList.indexOf(this.technology)}`;
		},
		getWorkflow() {
			return this.$store.state.hcube.workflows[this.getWorkflowKey];
		},
		activeWorkflowTags() {
			return this.$store.getters['tags/activeWorkflowTags'];
		},
		activeStepTags() {
			return this.$store.getters['tags/activeStepTags'];
		},
		pdfHeaderData() {
			const data = this.$store.state;

			return {
				mode: 'Config. Mode',
				title: data.workflowHeadline ? data.workflowHeadline : data.workflowDefaultHeadline,
				owner: data.jwt ? data.jwt.firstName + ' ' + data.jwt.lastName : ' - ',
				modifyDate: data.lastModDate ? data.lastModDate : ' - ',
				breadcrumb: data.workflowDefaultHeadline,
			};
		},
		registerLink() {
			return this.$store.state.registrationLink.replace('_CURRENT_URL_', window.location.href);
		},
		restoreOptionsModal() {
			return !!(this.optionsModalKey && this.$store.state.jwt && this.workflowsLoaded);
		},
		updateShareableLink() {
			return this.shareableLink;
		},
	},
	async created() {
		this.$root.$emit('closeGlobalBanner');
		const thisUrl = new URL(window.location.href);

		this.$store.commit('tags/setAssignments', {});
		this.$store.dispatch('tags/resetActiveTags', {setDefaultTags: false});

		// if called from shared url:
		if (thisUrl.href.includes('/shared/')) {
			const snapshotUrl = thisUrl.href.replace('/shared/', '/api/shared_workflows/');

			// define method for status 200
			const resolveSnapshotRequest = (data) => {
				const sharedWf = data.snapshot;
				sharedWf.data = sharedWf.document;
				delete sharedWf.document;
				const {
					data: {entryPoints},
				} = sharedWf;

				// standardize the url and set the workflow data in the $store
				const standardUrl = this.transformSharedUrl(thisUrl.href, entryPoints);
				history.pushState({}, null, standardUrl);
				const setStore = async () => {
					this.$store.state.isSharedWorkflow = true;
					await this.setWorkflowDataToStore(sharedWf);
				};
				setStore().then(() => {
					// open the shared workflow
					this.$router.push({
						name: 'Workflow',
						params: {
							application: entryPoints.application,
							startingMaterial: entryPoints.startingMaterial,
							analyte: entryPoints.analyte,
							technology: entryPoints.technology,
						},
					});
					this.$bus.$emit('updateSharedTitle');
					this.isSharedWorkflow = true;
				});
			};

			// define method when status is error
			const rejectSnapshotRequest = (error) => {
				if (error.response.status === 404) this.$store.commit('setNoWorkflowFound', 'snapshotExistsError');
				if (error.response.status >= 500) this.$store.commit('setNoWorkflowFound', 'snapshotDisplayError');
			};

			// call workflow snapshot api
			this.callToApiGetSnapshot(snapshotUrl, 'get')
				.then((data) => resolveSnapshotRequest(data))
				.catch((error) => rejectSnapshotRequest(error));
		}

		// if called from MyWorkflows view:
		if (thisUrl.searchParams.get('workflowId')) {
			const workflowId = thisUrl.searchParams.get('workflowId');
			await this.saveMyWorkflowIdToLocalStorage(workflowId);
			const trimmedUrl = `${window.location.origin}/workflow-configurator${this.$route.path}`;
			history.pushState({}, null, trimmedUrl);
		}

		// check url for params
		if (!(this.application && this.analyte && this.startingMaterial && this.technology))
			return this.noWorkflowsFound(true);

		await this.$store.dispatch('fetchUrls');
		await this.$store.dispatch('fetchHelperFiles');
		await this.$store.dispatch('fetchCategories');
		await this.$store.dispatch('checkPublicationIds');

		if (!this.showRequestsModal) {
			this.sendToAnalytics('workflow overview');
		} else {
			const pageType = !this.isGenomic ? 'contact sales' : 'request genomic services';
			this.sendToAnalytics(pageType);
		}

		const application = this.findSelection('Application', this.application);
		const analyte = this.findSelection('Analyte', this.analyte);
		const startingMaterial = this.findSelection('Starting material', this.startingMaterial);
		const technology = this.findSelection('Analysis type', this.technology);

		this.loading = false;

		// not able to find a workflow with the entry points in url
		if (!(application && analyte && startingMaterial && technology)) return this.noWorkflowsFound(true);

		this.$store.commit('setCategorySelection', {category: 'application', selection: application});
		this.$store.commit('setCategorySelection', {
			category: 'startingMaterial',
			selection: startingMaterial,
		});
		this.$store.commit('setCategorySelection', {category: 'analyte', selection: analyte});
		this.$store.commit('setCategorySelection', {category: 'technology', selection: technology});

		// builds default workflow headling from selected categories
		this.$store.dispatch('buildWorkflowDefaultHeadline');

		this.$root.$on('wantToLeaveModal', () => {
			this.openWantToLeaveModal = true;
		});

		this.setGenomic();

		this.$root.$on('unsetGenomic', this.unsetGenomic);
		this.$root.$on('runExclusiveExcludeRules', this.filterStepsInit);
		// user reloads page, adds myworkflow id to localstorage
		window.addEventListener('beforeunload', this.beforeWindowUnload);
		const myWfId = this.getPropertyFromLocalStorage('myWfId');
		myWfId && (await this.restoreFromLsMyWorkflows(myWfId));
		await this.init();
	},
	mounted() {
		this.checkIfSentToLogin();
		this.checkSavedWorkflow();
	},
	methods: {
		async onTagsSelectionChange(fillWithPreviousSelection = true) {
			if (this.pageIsReady) {
				this.pageIsReady = false;
				const wfSteps = clonedeep(this.$store.getters['workflow/wfSteps']);
				const customSteps = wfSteps.reduce((acc, step, index) => {
					if (step.customContent && !step.createdOnInit) acc.push({index, step});
					return acc;
				}, []);
				const allSteps = await this.buildWorkFlowSteps(this.getWorkflow, true);
				allSteps.sort((a, b) => a.index - b.index);
				let workflowSteps = [...allSteps];
				if (fillWithPreviousSelection) {
					workflowSteps = allSteps.map((step) => {
						const oldStep = wfSteps.find((s) => s.name === step.name);
						let newStep = this.fillSelectedWithCustomProducts(step, oldStep);
						if (oldStep) newStep.stepEnabled = oldStep.stepEnabled;
						return newStep;
					});
				}
				workflowSteps = await this.$store.dispatch('wfRules/runRules', workflowSteps);
				customSteps.forEach(({index, step}) => {
					workflowSteps.splice(index, 0, step);
				});
				await this.$store.dispatch('workflow/setWfSteps', workflowSteps);
				this.pageIsReady = true;
			}
		},
		fillSelectedWithCustomProducts(step, oldStep) {
			if (!step.customContent && oldStep) {
				const customProducts = oldStep.products.reduce((acc, product) => {
					if (product.customContent) {
						acc.push(product);
					}
					return acc;
				}, []);
				if (customProducts.length) {
					const selectedCustomProductsKeys = customProducts.map((p) => ({initial: p.key, updated: p.key}));
					return {
						...step,
						products: [...step.products, ...customProducts],
						selected: [...step.selected, ...selectedCustomProductsKeys],
					};
				}
			}
			return step;
		},
		productTags(product) {
			return this.$store.getters['tags/productTagAssignments'](product);
		},
		productWorkflowTags(product) {
			let activeWorkflowTags = this.$store.getters['tags/activeWorkflowTags'];
			let activeGroupIds = Object.keys(activeWorkflowTags).map((id) => Number.parseInt(id));
			let retval = this.productTags(product)
				.filter((tag) => activeGroupIds.includes(tag['group-id']))
				.reduce((acc, tag) => {
					if (!acc[tag['group-id']]) acc[tag['group-id']] = [];
					acc[tag['group-id']].push(tag.id);
					return acc;
				}, {});
			return retval;
		},
		matchesWorkflowTagsSelection(product) {
			let activeWorkflowTags = this.$store.getters['tags/activeWorkflowTags'];
			const productWorkflowTags = this.productWorkflowTags(product);
			let productHasNoTags = Object.keys(productWorkflowTags).length === 0;
			if (productHasNoTags) {
				return true;
			}
			return !Object.keys(productWorkflowTags)
				.map((groupId) => {
					let noSelectedTagsInGroup = activeWorkflowTags[groupId].size === 0;
					let productHasSelectedTagInGroup = productWorkflowTags[groupId].some((tag) =>
						activeWorkflowTags[groupId] ? activeWorkflowTags[groupId].has(tag) : false,
					);
					return noSelectedTagsInGroup || productHasSelectedTagInGroup;
				})
				.includes(false);
		},
		productStepTags(product, step) {
			let activeStepTags = this.$store.getters['tags/activeStepTags'][step] || {};
			let activeGroupIds = Object.keys(activeStepTags).map((id) => Number.parseInt(id));
			let retval = this.productTags(product)
				.filter((tag) => activeGroupIds.includes(tag['group-id']))
				.reduce((acc, tag) => {
					if (!acc[tag['group-id']]) acc[tag['group-id']] = [];
					acc[tag['group-id']].push(tag.id);
					return acc;
				}, {});
			return retval;
		},
		matchesStepTagsSelection(product, step) {
			let activeStepTags = this.$store.getters['tags/activeStepTags'][step] || {};
			const productStepTags = this.productStepTags(product, step);
			let productHasNoTags = Object.keys(productStepTags).length === 0;
			if (productHasNoTags) {
				return true;
			}
			return !Object.keys(productStepTags)
				.map((groupId) => {
					let noSelectedTagsInGroup = activeStepTags[groupId].size === 0;
					let productHasSelectedTagInGroup = productStepTags[groupId].some((tag) =>
						activeStepTags[groupId] ? activeStepTags[groupId].has(tag) : false,
					);
					return noSelectedTagsInGroup || productHasSelectedTagInGroup;
				})
				.includes(false);
		},
		openResetWorkflowModal() {
			this.isResetWorkflowModalOpen = true;
		},

		closeResetWorkflowModal() {
			this.isResetWorkflowModalOpen = false;
		},

		async resetWorkflow() {
			await this.$store.dispatch('workflow/setWfSteps', this.$store.getters['workflow/wfStaticSteps']);
			this.$store.dispatch('tags/resetActiveTags', {setDefaultTags: true});
			await this.onTagsSelectionChange();
			this.$store.commit('clearStepProductFilters');
			this.isResetWorkflowModalOpen = false;
		},

		checkSavedWorkflow() {
			this.isSavedWorkflow = !!this.getMyWorkflowFromLocalStorage();
			this.$store.state.isSavedWorkflow = this.isSavedWorkflow;
		},

		async validateAndAddWorkflowStep() {
			const validate = await this.$refs.customStep.validate().then((success) => success);
			if (!validate) return;
			this.addWorkflowStep();
		},

		beforeWindowUnload() {
			const myWfId = this.$store.state.myWorkflow?.id || this.$store.state.workflowID;
			myWfId && this.saveMyWorkflowIdToLocalStorage(myWfId);
		},
		checkClosePopup(ifFn = () => {}, elseFn = () => {}) {
			if (this.optionsModalIsOpen || this.showRequestsModal) {
				if (this.optionsModalIsOpen) this.toggleOptionsModal();
				if (this.showRequestsModal) this.requestBackToWorkflow();
				ifFn(false);
			} else {
				elseFn();
			}
		},
		interestsLink(link) {
			return link
				.replace('_REGISTRATION_LINK_', this.$store.state.registrationLink)
				.replace('_CURRENT_URL_', window.location.href);
		},
		sendToAnalytics(pageName) {
			const isLoggedIn = this.getLoggedInStatus;
			const s_dtm = createObject(pageName, this.getCountry, isLoggedIn, this.getUserId());
			trackObject(s_dtm);
		},
		tracking(link) {
			if (link == 'Register now') {
				trackEvent('ctaClick', {eVar58: 'Register | Workflow Configurator'});
			} else if (link == 'read more') {
				trackEvent('ctaClick', {eVar58: 'Our stories (Read more) | Workflow Configurator'});
			} else if (link == 'discover more') {
				trackEvent('ctaClick', {eVar58: 'Ordering from QIAGEN (Discover more) | Workflow Configurator'});
			}
		},
		async init() {
			const staticSteps = await this.buildWorkFlowSteps(this.getWorkflow);
			staticSteps.sort((a, b) => a.index - b.index);
			const allProducts = staticSteps.reduce((productsArray, step) => {
				return step.products ? [...productsArray, ...step.products] : productsArray;
			}, []);
			await this.$store.dispatch('workflow/setAllProducts', allProducts);

			const stepsProducts = staticSteps.reduce((acc, step) => {
				return {...acc, [step.name]: step.products || []};
			}, {});
			await this.$store.dispatch('workflow/setStepsProducts', stepsProducts);

			const productKeys = this.getWorkflow.steps.reduce((acc, step) => {
				acc.push(...step.p);
				return acc;
			}, []);
			if (this.isSavedWorkflow || this.isSharedWorkflow) {
				const publicationId = this.$store.state.tags.storedTags.publicationId;
				await this.$store.dispatch('tags/fetchTags', {publicationId});
				await this.$store.dispatch('tags/fetchTagAssignments', {
					products: productKeys,
					publicationId,
				});
				await this.restoreSavedWorkflowTags();
			} else {
				await this.$store.dispatch('tags/fetchTags');
				await this.$store.dispatch('tags/fetchTagAssignments', {products: productKeys});
			}

			const allSteps = await this.buildWorkFlowSteps(this.getWorkflow, true);
			allSteps.sort((a, b) => a.index - b.index);
			this.prevSelectedProducts = this.getSelectedProducts(allSteps);
			await this.$store.dispatch('workflow/setWfSteps', allSteps);

			await this.$nextTick();

			// update steps with data from myWorkflow
			if (this.$store.state.myWorkflow || this.getPropertyFromLocalStorage('myWfId'))
				await this.updateStepsFromMyWorkflowInit();

			// update steps with data from local storage
			if (
				!this.$store.state.myWorkflow &&
				this.$store.state.supportsLocalStorage &&
				this.getPropertyFromLocalStorage('lsWorkflow') &&
				!this.getPropertyFromLocalStorage('myWfId')
			) {
				await this.restoreFromLocalStorage(this.getPropertyFromLocalStorage('lsWorkflow'));

				const publicationId = this.$store.state.tags.storedTags.publicationId;
				await this.$store.dispatch('tags/fetchTags', {publicationId});
				await this.$store.dispatch('tags/fetchTagAssignments', {
					products: productKeys,
					publicationId,
				});
				await this.restoreSavedWorkflowTags();
			}

			// if there are no workflows, send to home view
			if (!this.$store.getters['workflow/wfSteps']) return this.noWorkflowFound();

			// choose other actions to take when user comes back from login
			if (this.$store.state.supportsLocalStorage && this.checkIfLoggedIn()) {
				// saves when user comes back from login
				if (localStorage.getItem('saveOnReturn')) {
					this.saveForLoggedInUser();
				}

				// shares workflow when user comes back from login
				if (localStorage.getItem('shareOnReturn')) {
					localStorage.removeItem('shareOnReturn');
					this.openShareWorkflowModal();
				}

				// sets the lab notes flag to active when user comes back from login
				if (localStorage.getItem('setLabNotesActiveOnReturn')) {
					localStorage.removeItem('setLabNotesActiveOnReturn');
					this.labNotesActive = true;
				}
			}

			// TBD: UPDATE TO $STORE
			const workflowSteps = await this.$store.dispatch('wfRules/runRules');
			this.$store.dispatch('restoreWorkflow/isRestoredWorkflow', false);
			await this.$store.dispatch('workflow/setWfSteps', workflowSteps);
			// set main and static workflows
			await this.$store.dispatch('workflow/setWfStaticSteps', staticSteps);

			// get customer prices
			if (this.$store.state.jwt) await this.$store.dispatch('customerPrices/fetchCustomerPrices')
			this.pageIsReady = true;
		},
		buildCustomStep(step) {
			const stepKey = this.$store.state.hcube.stepsList[0][step.i];
			const foundStep = this.storeStepsList.find((el) => el.key === stepKey);
			return {
				index: foundStep?.index ?? 0,
				name: stepKey,
				stepEnabled: true,
				labNotes: {desc: ''},
				customContent: {
					title: 'Description',
					desc: 'Please add a description for your step',
					customProduct: false,
				},
			};
		},
		async buildWorkFlowSteps(workflow, filterByTags = false) {
			if (!workflow) return this.noWorkflowsFound(true); // no workflow found
			const stepsList = this.$store.state.hcube.stepsList[0]; // cache stepsLists

			// build out each step obj
			const allSteps = [];
			workflow.steps.forEach((wfs) => {
				let step;
				let allProducts = wfs.p;
				const stepName = stepsList[wfs.i];
				const fallbackImageLink = this.$store.state.fallbackImageLink;

				if (allProducts?.length) {
					// add default and required products
					allProducts = this.buildProducts(allProducts);
					if (!allProducts?.length && !wfs.f) return;
					if (filterByTags) {
						allProducts = allProducts
							.filter((p) => this.matchesWorkflowTagsSelection(p))
							.filter((p) => this.matchesStepTagsSelection(p, stepName));
					}

					// if there are no valid products and product is not forced don't add it to workflowsteps

					// add fallback image for products without an image url
					for (const product of allProducts) {
						if (!product.image || !product.image.length) {
							product.image = fallbackImageLink;
						}
						if (!product.mobileImage || !product.mobileImage.length) {
							product.image = fallbackImageLink;
						}
					}

					// get selected products
					// TBD: find if selected products will ever be forced
					const selected = this.getSelectedOption(allProducts);
					const foundStep = this.storeStepsList.find((el) => el.key === stepsList[wfs.i]);
					step = {
						index: foundStep?.index ?? 0,
						name: stepsList[wfs.i],
						products: allProducts,
						stepEnabled: true,
						selected,
						labNotes: {desc: ''},
						filterCategories: this.getFilterCategories(allProducts),
					};
				} else {
					step = this.buildCustomStep(wfs);
					step.createdOnInit = true;
				}
				allSteps.push(step);
			});
			// commit workFlowSteps to store
			return allSteps;
		},
		buildProducts(productKeys) {
			// remove duplicates > get product obj > filter by country visability
			return [...new Set(productKeys)]
				.map((step) => {
					if (!this.$store.state.productsList[step]) return;
					const prod = getWebshopData(this.$store.state.productsList[step], this.$store.state.COUNTRY);
					return this.addRuleProps(prod);
				})
				.filter(this.filterProductsByCountry);
		},
		requestDemo() {
			trackEvent('ctaClick', {eVar58: 'Request a demo | Workflow Configurator'});
			const demoRequestLink = this.$store.state.demoRequestLink;
			window.open(demoRequestLink, '_blank');
		},
		sendToInquireLink(itemType) {
			trackEvent('ctaClick', {eVar58: 'Inquire | Workflow Configurator'});

			const inquireInstrumentLink = this.$store.state.inquireInstrumentLink.replace(
				'_COUNTRY_',
				this.getCountry.toLowerCase(),
			);
			const inquireStandardLink = this.$store.state.inquireStandardLink;

			const link =
				itemType === 'Instrument'
					? inquireInstrumentLink
					: `${inquireStandardLink}?requestType=quote&country=${this.getCountry}&state=&loginid=`;

			window.open(link, '_blank');
		},
		sendToGeneGlobeLink(articleNumber) {
			trackEvent('ctaClick', {eVar58: 'Gene Globe| Workflow Configurator'});
			let link = this.$store.state.geneGlobeLink;
			link = link.replace('_COUNTRY_', this.getCountry.toLowerCase());
			link = link.replace('_ARTICLE_NUMBER_', articleNumber);
			window.open(link, '_blank');
		},
		showCustomProductModal(obj) {
			if (!obj) return;
			const {task, value, key, stepName, productIndex} = obj;
			this.customProductData = {task, desc: value, key, stepName, productIndex};
			this.openCustomProductModal = !this.openCustomProductModal;
		},
		async setCustomProductVal(obj) {
			if (!obj) return;
			const {task, desc, key, stepName, productIndex} = obj;
			const wfs = await clonedeep(this.$store.getters['workflow/wfStep'](stepName));

			const customProduct = this.buildAddStep({
				name: this.modalOptions.name,
				desc,
				customProduct: true,
				title: 'Your Own Product',
				key,
			});
			const addProduct = key;
			const removeProduct = wfs.selected[productIndex]?.initial || null;

			wfs.products.push(customProduct);
			wfs.selected[productIndex] = {
				initial: key,
				updated: key,
			};

			this.doWeShowRuleModal({wfs, addProduct, removeProduct});
		},
		setLabNotesActive(bool) {
			if ((bool && !this.checkIfLoggedIn()) || (bool && this.isJwtExpired())) {
				if (this.$store.state.supportsLocalStorage) {
					localStorage.setItem('setLabNotesActiveOnReturn', true);
				}
				this.resetJwtCookie();
				this.labNotesSelected = true; // select preLogInData
				this.preLoginModal = true; // open preLogIn modal
			} else {
				this.labNotesActive = bool;
			}
			// tour
			if (bool) {
				this.setUpElnTour();
			} else {
				this.startTour('workflow');
			}
		},
		addRuleProps(prod) {
			if (!prod) return;
			return {
				...prod,
				rules: {
					excludeBy: new Set(),
					exclusiveBy: new Set(),
					exclusiveRequiredBy: new Set(),
					requiredBy: new Set(),
					suggestedBy: new Set(),
				},
			};
		},
		filterProductsByCountry(prod) {
			if (!prod) return false;
			// remove any products that:

			// are not wfc ready
			if (Object.hasOwnProperty.call(prod, 'is_wfc_ready') && !prod.is_wfc_ready) return false;

			// are not published
			if (!prod.publishingStatus === 'PUBLISHED') return false;

			// are not available in country
			if (!prod.visibility.includes(this.$store.state.COUNTRY)) return false;

			// considered phased out and phasing out date is before today
			if (prod.phasingOut) {
				// if phasedOut and doesn't have phasingOutDate
				if (!prod.phasingOutDate) return false;
				const phDate = new Date(prod.phasingOutDate * 1000);
				const today = new Date();
				// if phasingOut is true and the date is before
				if (phDate.getTime() < today.getTime()) return false;
			}
			return true;
		},
		getFilterCategories(products) {
			/*
        Build Sets() for each product category
        ie: Application: Set(3){"cloning", "automateddnafragmentandrnaanalysis", "fusiongene"}
      */
			const filterSets = this.getFilterSets(products);

			/**
			 *  Builds filterCategories for each workflowstep
			 */
			const filterCategories = {};
			for (const filterKey in filterSets) {
				if (filterKey) {
					// name of Set()
					// find where the value.key === name of Set() ie: Application === Application
					const filterGroup = this.$store.state.cvlFilter.values.find((value) => value.key === filterKey);

					// update order of filterSets
					const filterSetSorted = [...filterSets[filterKey]].sort();

					// add children that are also in the filterSet
					const filterGroupChildArry = filterGroup.children.filter((child) =>
						filterSetSorted.includes(child.key),
					);
					filterCategories[filterGroup.key] = {
						key: filterGroup.key,
						name: filterGroup.value,
						filterGroup: {
							filters: filterGroupChildArry,
							selectedFilters: {
								temp: [],
								updated: [],
							},
						},
					};
				}
			}
			return filterCategories;
		},
		getFilterSets(products) {
			const filterSets = {};
			// for each product, build filter sets by category
			products.forEach((product) => {
				// each product in a workstep
				product.filterCategories.forEach((category) => {
					// Create a Set for each filterCategories.catergory.key; ie: Application, Instrument, Detction
					if (!Object.prototype.hasOwnProperty.call(filterSets, category.key)) {
						filterSets[category.key] = new Set();
					}
					if (category.children) {
						category.children.forEach((child) => {
							// Add category to set ie:
							// Application: Set(3){"cloning", "automateddnafragmentandrnaanalysis", "fusiongene"}
							filterSets[category.key].add(child.key);
						});
					}
				});
			});
			return filterSets;
		},
		getSelectedOption(allProducts) {
			/*
        needs to be created to check for options for preselected product that
        might come for the url,

        else return 0 for index referance
      */
			return allProducts.length
				? [
						{
							initial: allProducts[0].key,
							updated: allProducts[0].key,
						},
				  ]
				: [];
		},
		triggerCustomProductModal() {
			this.openCustomProductModal = !this.openCustomProductModal;
		},
		addQueryModal() {
			const {name} = this.$route;
			this.$router
				.replace({name, query: {modal: 'open'}})
				.then(() => {})
				.catch(() => {});
		},
		removeQueryModal() {
			const {name} = this.$route;
			this.$router
				.replace({name})
				.then(() => {})
				.catch(() => {});
		},
		toggleOptionsModal(workflow) {
			const isLoggedIn = this.getLoggedInStatus;
			const s_dtm = createObject('product option', this.getCountry, isLoggedIn, this.getUserId());
			trackObject(s_dtm);

			if (workflow && !this.optionsModalIsOpen) {
				const wfs = clonedeep(this.$store.getters['workflow/wfStep'](workflow.name));
				this.modalOptions = wfs || this.modalOptions;
				this.modalOptions.productIndex = workflow.productIndex;
				this.optionsModalIsOpen = true;
				this.addQueryModal();
				this.$el.closest('body').classList.add('overflow-hidden');
			} else {
				this.optionsModalIsOpen = false;
				this.removeQueryModal();
				this.$el.closest('body').classList.remove('overflow-hidden');
			}
		},
		showMyWorkflows() {
			if (this.checkIfLoggedIn() && !this.isJwtExpired()) {
				this.myWorkflowsView = false;
				this.$router.push({path: '/myWorkflows'}).catch(() => {});
			} else {
				this.preLoginModal = true;
				this.myWorkflowsView = true;
			}
		},
		sendToLogin(saveOnReturn = true, returnToView) {
			// TBD: move to mixins
			const loginLink = this.$store.state.loginLink;
			if (this.$store.state.supportsLocalStorage) {
				// set sent to login
				localStorage.setItem('sentToLogin', true);
				// set save on retun
				if (saveOnReturn && !(this.requestQuoteSelected || this.requestGenomicSelected)) {
					localStorage.setItem('saveOnReturn', true);
				} else {
					localStorage.removeItem('saveOnReturn');
				}
				this.saveChangeToLocalStorage();
			}

			let view = '';
			if (this.myWorkflowsView) {
				view = `${window.location.origin}/workflow-configurator/myWorkflows`;
			} else {
				view = returnToView
					? `${window.location.origin}/workflow-configurator/${returnToView}`
					: `${window.location.href}`;
			}
			window.location = `${loginLink}${view}`;

			this.myWorkflowsView = false;
			this.$emit('sentToLogin');
		},
		logInFromOptionsModal(key) {
			this.optionsModalKey = key;
			this.sendToLogin(false);
		},
		restoreOptionsModalAfterLogin() {
			for (const obj of this.renderWorkFlowSteps) {
				if (obj.products) {
					for (const product of obj.products) {
						if (product.key == this.optionsModalKey) {
							this.toggleOptionsModal(obj);
							break;
						}
					}
				}
			}
		},
		async noWorkflowsFound(badParams) {
			const selection = null;
			let bannerVersion = null;
			const noWfFound = this.$store.state.noWorkflowFound;

			// if a snapshot banner was not set, then set banner version to show
			if (!noWfFound.toString().includes('snapshot')) {
				bannerVersion = badParams ? 'noWorkflowsFound' : 'emptyWorkflow';
				this.$store.commit('setNoWorkflowFound', bannerVersion);
			}

			// reset categories
			['analyte', 'application', 'startingMaterial', 'technology'].forEach((category) => {
				this.$store.commit('setCategorySelection', {category, selection});
			});

			// reset localStorage
			localStorage.clear();

			// reset state
			await this.$store.commit('setWorkflowHeadline', null);
			await this.$store.commit('setWorkflowDefaultHeadline', null);
			await this.$store.commit('setWorkFlowId', null);
			await this.$store.commit('setMyWorkflow', null);
			await this.$store.commit('setMyWorkflows', null);
			await this.$store.commit('setLastModDate', null);

			// send to home view
			this.$router.replace({name: 'Home'}).catch(() => {});
		},
		checkIfSentToLogin() {
			if (localStorage.getItem('sentToLogin') && this.$store.state.jwt) {
				document.cookie = `COUNTRY=${this.$store.state.jwt.country}; domain=.qiagen.com; expires=${new Date(
					2147483647 * 1000,
				).toUTCString()}; path=/; secure`;
				localStorage.removeItem('sentToLogin');
			}
		},
		setGenomic() {
			let genomic = false;
			try {
				genomic = JSON.parse(localStorage.getItem('isGenomic'));
			} catch (error) {
				loggingDevInfo(error);
			}

			if (genomic) {
				return (this.isGenomic = genomic);
			}
			this.isGenomic = false;
			localStorage.removeItem('isGenomic');
		},
		unsetGenomic() {
			this.isGenomic = false;
			localStorage.removeItem('isGenomic');
		},
		requestBackToWorkflow() {
			this.showRequestsModal = false;
			this.$bus.$emit(this.$bus.CLEAR_SNACKBAR);
		},
		getUserId() {
			const jwt = this.$store.state.jwt;
			if (jwt) return jwt.sub;
			return 'not loggedin';
		},
		openPreLoginModal() {
			this.preLoginModal = true;
		},
		openShareWorkflowModal() {
			if (this.checkIfLoggedIn() && !this.isJwtExpired()) {
				this.$store.commit(
					'featureShare/openModal',
					JSON.parse(buildLsObjForBackend(this.wfApiInfo, this.$store.state.hcube.publication_id)),
				);
			} else {
				if (this.$store.state.supportsLocalStorage) {
					localStorage.setItem('shareOnReturn', true);
				}
				this.resetJwtCookie();
				this.shareWorkflowSelected = true; // use preLoginData for shareWorkflow
				this.preLoginModal = true; // open preLogin modal
			}
		},
		transformSharedUrl(sharedUrl, entryPoints) {
			let xformUrl = sharedUrl.substring(0, sharedUrl.search('shared/')).concat('workflows/');
			const entryPointProps = [
				entryPoints.application,
				entryPoints.startingMaterial,
				entryPoints.analyte,
				entryPoints.technology,
			];
			entryPointProps.forEach((item) => {
				xformUrl = xformUrl.concat(item + '/');
			});
			xformUrl.replace('shared', 'workflows');
			return xformUrl.substring(0, xformUrl.length - 1);
		},
		async setWorkflowDataToStore(workflow) {
			const lastModDate = new Date(workflow.data.last_mod_date);
			await this.$store.commit('setLastModDate', lastModDate.toLocaleDateString());
			await this.$store.commit('setWorkFlowId', workflow.id);
			await this.$store.commit('setWorkflowHeadline', workflow.title);
			await this.$store.commit('setMyWorkflow', workflow);
			this.$root.$emit('updateMyWfHeader');
			await this.$store.commit('tags/setTagsToBeRestored', {
				...workflow.data.activeTags,
				publicationId: workflow.data.publication_id,
			});
			await this.$nextTick();
		},
		async restoreSavedWorkflowTags() {
			const storedTags = this.$store.state.tags.storedTags;
			const storedWorkflowTags = {...storedTags.workflow};

			let workflowTags = Object.keys(storedWorkflowTags).reduce((acc, groupId) => {
				const tagEntries = storedWorkflowTags[groupId].map((tagId) => {
					return {'group-id': parseInt(groupId), 'id': tagId};
				});
				acc.push(...tagEntries);
				return acc;
			}, []);
			await this.$store.commit('tags/clearActiveTags');
			await this.$store.commit('tags/clearActiveStepTags');
			await this.$store.commit('tags/setActiveTags', workflowTags);
			const storedStepsTags = {...storedTags.step};
			const stepsTags = Object.keys(storedStepsTags).reduce((stepAcc, stepName) => {
				const storedStepTags = {...storedStepsTags[stepName]};
				let tags = Object.keys(storedStepTags).reduce((groupAcc, groupId) => {
					const tagEntries = storedStepTags[groupId].map((tagId) => {
						return {step: stepName, tag: {'group-id': parseInt(groupId), 'id': tagId}};
					});
					groupAcc.push(...tagEntries);
					return groupAcc;
				}, []);
				stepAcc.push(...tags);
				return stepAcc;
			}, []);
			await this.$store.commit('tags/setActiveStepTags', stepsTags);
		},
		beforeDestroy() {
			window.removeEventListener('beforeunload', this.beforeWindowUnload);
		},
	},
};
</script>

<style lang="scss" scoped>
.md-snackbar.md-position-left {
	@media (max-width: $mobile) {
		bottom: 76px;
		left: 10px;
	}
}

.workflow-view {
	min-height: 100vh;
	height: 100%;

	.workflow-body {
		@include box(100%);
	}

	.workflow-step {
		&:last-of-type .add-workflow-step {
			.line,
			.arrow {
				display: none;
			}
		}
	}

	.cta-section {
		header {
			background-color: $color-super-light-blue;
			border: 1px solid $color-light-grey;
			border-left: none;
			border-right: none;
			padding: 14px 16px 13px;

			h5 {
				margin: 0;
			}
		}

		.cta-row {
			background: $white;
			padding: 0.5rem;
			display: flex;
			flex-wrap: wrap;

			.base-cta {
				width: calc(50% - 16px);
				margin: 8px;
			}
		}

		@media (max-width: $mobile) {
			margin-top: 62px;
		}
		@media (max-width: 800px) {
			.cta-row {
				.base-cta {
					width: 100%;
				}
			}
		}
	}

	.interests {
		&-section {
			margin: 72px 0 15px;

			&.is-lab-note-mode {
				margin: 0px 0 15px;
			}
		}

		&-header {
			h5 {
				margin-bottom: 16px;
			}
		}

		&-body {
			@include flexRow;
		}

		&-card {
			@include flexColumn;
			background-color: $color-extra-light-grey;
			flex: 1;
			margin-bottom: 24px;

			&:not(:last-child) {
				margin-right: 24px;
			}

			.interests-card-details {
				@include flexColumn;
				height: 100%;
				align-items: self-end;
				padding: 12px 24px;
				background: $white;

				p {
					flex-grow: 1;
				}
			}

			img {
				height: 223px;
				width: 100%;
				object-fit: cover;
				object-position: top center;
			}
		}

		@media (max-width: 800px) {
			&-body {
				flex-wrap: wrap;
				justify-content: space-between;
			}

			&-card {
				flex: 0 0 48.5%;

				&:not(:last-child) {
					margin-right: 0;
				}
			}
		}
		@media (max-width: $mobile) {
			&-body {
				display: block;
			}
		}
	}

	.add-workflow-step-modal {
		h3 {
			margin: 0;
		}

		.subHeader {
			margin-bottom: 10px;
		}

		.md-field {
			margin-bottom: 37px;

			.input {
				white-space: pre-wrap;
			}

			.textarea {
				max-height: 40vh;
			}
		}

		.add-step-footer {
			margin-top: 1rem;
			@include flexRow;
			width: 100%;
			align-items: center;
			justify-content: flex-end;

			button {
				@include flexRow;
				align-items: center;

				&:last-of-type {
					margin-left: 1rem;
				}
			}
		}
	}

	.slide-up-enter-active,
	.slide-up-leave-active {
		transform: translateY(0vh);
		transition: 0.5s;
	}

	.slide-up-enter, .slide-up-leave-to /* .fade-leave-active in <2.1.8 */ {
		transform: translateY(100vh);
	}
}
</style>

<style lang="scss">
/* printed media styles for the pdf */
.pdf-icon {
	display: none;
}

@media only print {
	@page {
		size: A4;
		margin: 1.6cm;
	}
	body {
		border: 12px solid transparent;
		background-color: $white !important;
		padding-top: 40px;
		padding-bottom: 40px;
		font-size: 0.7em;

		#globalBannerContainer {
			display: none;
		}

		wca-header,
		qia-footer {
			display: none;
		}

		.dropdown-button-content {
			display: none;
		}

		.md-snackbar {
			display: none;
		}

		.icons.base-icons.icons-add {
			display: none;
		}

		.workflow-app {
			display: inline;

			#nav {
				display: none;
			}

			.workflow-view {
				#pdf-header {
					@include font-styles('Futura W01 Medium', 500);
					display: block;
					color: $color-text-grey !important;
				}

				.workflow-header {
					display: none;
				}

				.workflow-body {
					margin-top: 40px;
					margin-bottom: 0px;

					.workflow-step[data-eln='active'] {
						/* set borders when lab notes become active */
						&:first-child {
							border-top: none;
						}

						&:last-child {
							border-bottom: 1px solid $black;
						}

						.workflow-container {
							&-header {
								border-bottom: 1px solid $black;
								border-top: 1px solid $black;
							}

							&-header[data-print='disabled'] {
								border-bottom: none;
							}

							.workflow-container-product-wrap {
								border-bottom: none;

								p.overline {
									display: none;
								}
							}

							.workflow-container-custom-content {
								border-bottom: none;
							}

							.qsp-accordion {
								display: none;
							}
						}
					}

					.workflow-step[data-eln='inactive'] {
						/* set borders when lab notes beome inactive */
						&:first-child {
							border-top: 1px solid $black;
						}

						.workflow-container {
							.workflow-container-product-wrap {
								border-bottom: 1px solid $black;
							}

							.workflow-container-custom-content {
								border-bottom: 1px solid $black;
							}
						}
					}

					.workflow-step {
						&:first-child {
							border-top: 1px solid $black;
						}

						&:last-child {
							margin-bottom: 24px;
						}

						.workflow-container {
							border-left: 1px solid $black;
							border-right: 1px solid $black;
							overflow: hidden;
							border-radius: 0;
							page-break-before: auto;
							page-break-inside: auto;
							page-break-after: auto;

							&-header {
								page-break-inside: avoid;
								border-top: none;
								border-bottom: 1px solid $black;
								border-radius: 0px;
								display: flex;
								justify-content: space-between;
								align-items: center;
								padding: 8px 16px;
								min-height: 17px;

								.left-side {
									max-width: 100%;

									header {
										h6 {
											font-size: 1.3em;
										}

										.custom-name {
											h6 {
												font-size: 1em;
											}
										}
									}
								}

								.right-side.toggle-switch {
									#add-product {
										display: none !important;
									}
								}
							}

							&-header[data-print='enabled'] {
								background-color: $color-text-grey !important;
								-webkit-print-color-adjust: exact;

								h6 {
									color: $white !important;

									span,
									div {
										color: $white !important;
									}
								}

								.pdf-icon {
									display: none;
								}

								.right-side.toggle-switch {
									#show-hide-step {
										display: none !important;
									}
								}
							}

							&-header[data-print='disabled'] {
								background-color: $color-extra-light-grey !important;
								-webkit-print-color-adjust: exact;

								h6 {
									color: $color-text-grey !important;

									span,
									div {
										color: $color-text-grey !important;
									}
								}

								.pdf-icon {
									display: inline;
									color: $color-grey !important;
									font-size: 1.1em;
								}

								.right-side.toggle-switch {
									#show-hide-step {
										div.md-ripple {
											div.md-button-content {
												div {
													span {
														display: none;
													}
												}
											}
										}
									}
								}
							}

							div {
								.workflow-container-product-wrap {
									border-bottom: 1px solid $black;
									padding: 0;

									.workflow-container-body {
										page-break-before: auto;
										page-break-inside: avoid;
										page-break-after: auto;
										display: flex;
										justify-content: flex-start;
										padding: 24px 24px 20px 24px;
										border: 0;
										width: 100%;

										.product-image {
											margin: 24px 24px 0px 0px;

											.image-wrap {
												.base-image {
													.base-image_box {
														width: 144px;
														height: 80px;
														top: -22px;
														filter: grayscale(100%);
													}
												}
											}
										}

										.product-content {
											display: inline-block;
											vertical-align: top;
											width: 100%;
											display: flex;
											justify-content: space-between;

											.desc {
												width: 100%;

												.product-title-wrap {
													.left {
														p.product-title {
															font-size: 1.1em;

															a {
																color: #000 !important;
															}

															a[href]:after {
																content: none !important;
															}
														}

														p.overline {
															font-size: 0.9em;

															span {
																display: none;
															}
														}

														.product-relations-wrap {
															display: none;
														}
													}
												}

												p.wf-container-caption {
													font-size: 1.1em;
													width: 100%;
												}
											}

											.product-ctas {
												display: inline;
												padding: 0;
												vertical-align: top;
												height: auto;
												width: 40%;

												button {
													display: none;
												}

												.price-style {
													position: relative;
													top: -6px;

													p {
														font-size: 1em;
													}

													.price-style-align {
														.caption-2 {
															position: relative;
															top: -6px;

															span {
																font-size: 0.8em;
															}
														}
													}
												}
											}
										}

										.product-options {
											display: none;

											button.md-button {
												div.md-ripple {
													display: none;
												}
											}
										}
									}

									.workflow-container-gg-cta {
										display: none;
									}
								}

								.workflow-container-custom-content {
									border-bottom: 1px solid $black;
									padding: 0;

									.workflow-container-body {
										.wf-body-column {
											width: 100%;
											margin: 0 !important;
											padding: 0 !important;
											height: auto !important;

											.add-step-textarea[data-print='disabled'] {
												display: none;
											}

											.printable-description {
												p {
													word-break: normal;
													padding: 16px 0;
													font-size: 14px !important;
													color: $color-text-grey !important;
													font-family: 'Futura W01 Book', Arial, Helvetica, sans-serif !important;
												}
											}

											.printable-description[data-print='enabled'] {
												word-break: normal;
												display: block !important;
												visibility: visible !important;
												height: 100% !important;
											}
										}
									}

									.is-custom-product {
										width: 100%;
										padding: 0px 24px 0px 24px;
										margin: 0;

										.custom-product-section {
											button {
												display: none;
											}
										}

										.product-options[data-print='disabled'] {
											height: 0 !important;
											visibility: hidden !important;
											display: none !important;
										}
									}
								}

								.lab-notes {
									padding: 0 24px !important;
									margin-top: 0;

									.printable-notes[data-print='enabled'] {
										visibility: visible;
										height: 100%;
										padding-bottom: 16px;
									}

									.md-field[data-print='disabled'] {
										display: none;
									}

									#lab-notes-area {
										.md-field[data-print='disabled'] {
											display: none;
										}
									}
								}
							}
						}

						.add-step-button-wrap.add-workflow-step {
							display: none;
						}
					}
				}
			}

			.cta-section {
				margin-top: -10px;

				&[data-print='enabled'] {
					display: block !important;
				}

				header {
					display: none;
				}

				.cta-1 {
					page-break-before: auto;
					page-break-inside: avoid;
					page-break-after: auto;
					display: flex !important;
					justify-content: space-between;
					padding: 0px 0px 24px 0px;

					.base-cta {
						page-break-inside: avoid;
						font-size: 1.4em;
						min-height: 120px;
						border: 1px solid #000000;
						border-radius: 0 !important;
						background-color: #ffffff !important;

						.content {
							padding: 16px 0px 20px 0px;

							.copy {
								page-break-inside: avoid;
								text-align: center;

								h5 {
									display: block;
									color: $color-text-grey !important;
								}

								p {
									display: inline-block;
									visibility: hidden;
									position: relative;
									font-size: 0.9em;
									color: $color-text-grey !important;
								}

								p:after {
									visibility: visible;
									position: absolute;
									left: 0;
									color: $color-text-grey !important;
									white-space: pre-wrap;
								}
							}

							.arrow {
								display: none;
							}
						}
					}

					.base-cta:not(:nth-child(1)):not(:nth-child(2)) {
						display: none;
					}

					.base-cta:nth-child(1) {
						width: 46%;

						.shopping-icon {
							display: inline;
							padding: 10px;
							position: relative;
							top: 2px;
						}

						p {
							margin-top: 18px;
							margin-bottom: 36px;
						}

						p:after {
							content: 'Select the products you would like to purchase. \A qiagen.com/products \A ';
						}
					}

					.base-cta:nth-child(2) {
						width: 48%;

						.content {
							.copy {
								h5 {
									position: relative;
									top: -1px;
								}
							}
						}

						.quote-icon {
							display: inline;
							padding: 10px;
							position: relative;
							top: 6px;
							font-size: 1.2em;
						}

						p {
							margin-top: -2px;
							margin-bottom: 36px;
						}

						p:after {
							content: 'Contact us if you would like to have products in a quote or a demo. \A go.qiagen.com/automation-request \A';
						}
					}
				}
			}

			.interests-section {
				display: none;
			}

			.warning-section {
				page-break-before: auto;
				page-break-inside: avoid;
				page-break-after: auto;
				margin-top: 20px;

				.workflow-disclaimer {
					padding: 0px 24px;
					margin-top: 0;

					p {
						font-size: 1.2em;
						display: grid;
						grid-gap: 4px;
						grid-template-columns: auto auto;
						color: $color-text-grey !important;

						.info-icon {
							position: relative;
							top: 1px;
							font-size: 1.2em;
							color: $color-text-grey !important;
						}
					}
				}
			}
		}
	}
}
</style>
<style>
@media only print {
	#esi-header,
	#esi-footer {
		display: none;
	}
}
</style>
